import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingState } from "@/components/ui/loading-state";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RotateCcw, ShieldCheck, ShieldOff, UserCheck, UserX } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface User {
	id: number;
	login: string;
	displayName: string;
	role: string;
	status: string;
	createdAt: number;
	lastLoginAt: number | null;
}

async function fetchUsers(): Promise<{ users: User[]; total: number }> {
	const res = await fetch("/api/users", { credentials: "include" });
	if (!res.ok) throw new Error("Failed to fetch users");
	return res.json();
}

async function apiCreateUser(data: {
	login: string;
	displayName: string;
	password: string;
	role?: string;
}) {
	const res = await fetch("/api/users", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(data),
	});
	if (!res.ok) {
		const err = await res.json();
		throw new Error(err.message || "Failed to create user");
	}
	return res.json();
}

async function apiUpdateUser(
	id: number,
	data: { displayName?: string; role?: string; status?: string },
) {
	const res = await fetch(`/api/users/${id}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(data),
	});
	if (!res.ok) {
		const err = await res.json();
		throw new Error(err.message || "Failed to update user");
	}
	return res.json();
}

async function apiResetPassword(id: number, newPassword: string) {
	const res = await fetch(`/api/users/${id}/reset-password`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ newPassword }),
	});
	if (!res.ok) {
		const err = await res.json();
		throw new Error(err.message || "Failed to reset password");
	}
	return res.json();
}

function formatDate(unix: number | null): string {
	if (!unix) return "—";
	return new Date(unix * 1000).toLocaleString("ru-RU");
}

export function UsersPage() {
	const queryClient = useQueryClient();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
	const [newPassword, setNewPassword] = useState("");

	// Create form state
	const [createLogin, setCreateLogin] = useState("");
	const [createDisplayName, setCreateDisplayName] = useState("");
	const [createPassword, setCreatePassword] = useState("");
	const [createRole, setCreateRole] = useState("user");

	const usersQuery = useQuery({
		queryKey: ["users"],
		queryFn: fetchUsers,
	});

	const createMutation = useMutation({
		mutationFn: apiCreateUser,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			setShowCreateForm(false);
			setCreateLogin("");
			setCreateDisplayName("");
			setCreatePassword("");
			setCreateRole("user");
			toast.success("Пользователь создан");
		},
		onError: (err: Error) => toast.error(err.message),
	});

	const updateMutation = useMutation({
		mutationFn: ({
			id,
			data,
		}: { id: number; data: { displayName?: string; role?: string; status?: string } }) =>
			apiUpdateUser(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			toast.success("Пользователь обновлён");
		},
		onError: (err: Error) => toast.error(err.message),
	});

	const resetPasswordMutation = useMutation({
		mutationFn: ({ id, password }: { id: number; password: string }) =>
			apiResetPassword(id, password),
		onSuccess: () => {
			setResetPasswordUserId(null);
			setNewPassword("");
			toast.success("Пароль сброшен");
		},
		onError: (err: Error) => toast.error(err.message),
	});

	const handleCreate = (e: React.FormEvent) => {
		e.preventDefault();
		createMutation.mutate({
			login: createLogin.trim(),
			displayName: createDisplayName.trim(),
			password: createPassword,
			role: createRole,
		});
	};

	const toggleStatus = (user: User) => {
		const newStatus = user.status === "active" ? "deactivated" : "active";
		updateMutation.mutate({ id: user.id, data: { status: newStatus } });
	};

	const users = usersQuery.data?.users ?? [];

	return (
		<div className="space-y-4">
			<PageHeader
				title="Управление пользователями"
				actions={
					<Button
						type="button"
						onClick={() => setShowCreateForm(!showCreateForm)}
						className="bg-blue-600 text-white hover:bg-blue-700"
					>
						<Plus className="h-4 w-4" />
						Новый пользователь
					</Button>
				}
			/>

			{showCreateForm && (
				<form
					onSubmit={handleCreate}
					className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
				>
					<h2 className="text-sm font-semibold text-slate-700">Создание пользователя</h2>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<label className="text-xs font-medium text-slate-600">Логин</label>
							<Input
								type="text"
								value={createLogin}
								onChange={(e) => setCreateLogin(e.target.value)}
								required
								pattern="^[a-zA-Z0-9_-]{3,30}$"
								className="mt-1"
								placeholder="login"
							/>
						</div>
						<div>
							<label className="text-xs font-medium text-slate-600">Отображаемое имя</label>
							<Input
								type="text"
								value={createDisplayName}
								onChange={(e) => setCreateDisplayName(e.target.value)}
								required
								className="mt-1"
								placeholder="Иван Иванов"
							/>
						</div>
						<div>
							<label className="text-xs font-medium text-slate-600">Пароль</label>
							<Input
								type="password"
								value={createPassword}
								onChange={(e) => setCreatePassword(e.target.value)}
								required
								minLength={8}
								className="mt-1"
							/>
						</div>
						<div>
							<label className="text-xs font-medium text-slate-600">Роль</label>
							<select
								value={createRole}
								onChange={(e) => setCreateRole(e.target.value)}
								className="mt-1 flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
							>
								<option value="user">Пользователь</option>
								<option value="admin">Администратор</option>
							</select>
						</div>
					</div>
					<div className="flex gap-2">
						<Button
							type="submit"
							size="sm"
							disabled={createMutation.isPending}
							className="bg-blue-600 text-white hover:bg-blue-700"
						>
							{createMutation.isPending ? "Создание..." : "Создать"}
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => setShowCreateForm(false)}
						>
							Отмена
						</Button>
					</div>
				</form>
			)}

			<div className="rounded-lg border border-slate-200 bg-white">
				{usersQuery.isLoading ? (
					<LoadingState />
				) : (
					<table className="w-full text-sm">
						<thead className="bg-slate-50 border-b border-slate-200">
							<tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
								<th className="px-4 py-3">Логин</th>
								<th className="px-4 py-3">Имя</th>
								<th className="px-4 py-3">Роль</th>
								<th className="px-4 py-3">Статус</th>
								<th className="px-4 py-3">Создан</th>
								<th className="px-4 py-3">Последний вход</th>
								<th className="px-4 py-3">Действия</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{users.map((user) => (
								<tr key={user.id} className="transition-colors hover:bg-slate-50">
									<td className="px-4 py-3 font-mono text-xs">{user.login}</td>
									<td className="px-4 py-3">{user.displayName}</td>
									<td className="px-4 py-3">
										<Badge
											variant="pill"
											tone={user.role === "admin" ? "info" : "neutral"}
											className="gap-1"
										>
											{user.role === "admin" ? (
												<ShieldCheck className="h-3 w-3" />
											) : (
												<ShieldOff className="h-3 w-3" />
											)}
											{user.role === "admin" ? "Админ" : "Пользователь"}
										</Badge>
									</td>
									<td className="px-4 py-3">
										<Badge
											variant="pill"
											tone={user.status === "active" ? "success" : "error"}
											className="gap-1"
										>
											{user.status === "active" ? (
												<UserCheck className="h-3 w-3" />
											) : (
												<UserX className="h-3 w-3" />
											)}
											{user.status === "active" ? "Активен" : "Деактивирован"}
										</Badge>
									</td>
									<td className="px-4 py-3 text-xs text-slate-500">{formatDate(user.createdAt)}</td>
									<td className="px-4 py-3 text-xs text-slate-500">
										{formatDate(user.lastLoginAt)}
									</td>
									<td className="px-4 py-3">
										<div className="flex items-center gap-1">
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() => {
													setResetPasswordUserId(user.id);
													setNewPassword("");
												}}
												title="Сбросить пароль"
												className="h-8 w-8 text-slate-400 hover:text-slate-600"
											>
												<RotateCcw className="h-4 w-4" />
											</Button>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() => toggleStatus(user)}
												title={user.status === "active" ? "Деактивировать" : "Активировать"}
												className={
													user.status === "active"
														? "h-8 w-8 text-slate-400 hover:bg-red-50 hover:text-red-600"
														: "h-8 w-8 text-slate-400 hover:bg-green-50 hover:text-green-600"
												}
											>
												{user.status === "active" ? (
													<UserX className="h-4 w-4" />
												) : (
													<UserCheck className="h-4 w-4" />
												)}
											</Button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{/* Reset password dialog */}
			{resetPasswordUserId !== null && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
					<div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
						<h3 className="mb-4 text-sm font-semibold text-slate-900">Сброс пароля</h3>
						<Input
							type="password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							placeholder="Новый пароль (мин. 8 символов)"
							minLength={8}
						/>
						<div className="mt-4 flex justify-end gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => setResetPasswordUserId(null)}
							>
								Отмена
							</Button>
							<Button
								type="button"
								size="sm"
								disabled={newPassword.length < 8 || resetPasswordMutation.isPending}
								onClick={() =>
									resetPasswordMutation.mutate({ id: resetPasswordUserId, password: newPassword })
								}
								className="bg-blue-600 text-white hover:bg-blue-700"
							>
								{resetPasswordMutation.isPending ? "Сброс..." : "Сбросить"}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
